import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/hooks/use-locale";

type Row = { namespace: string; key: string; value: string };

/** Loads all translation strings for a locale (with English fallback). */
function useStringsFor(locale: string) {
  return useQuery({
    queryKey: ["translations", locale],
    queryFn: async () => {
      const codes = locale === "en" ? ["en"] : [locale, "en"];
      const { data, error } = await supabase
        .from("translation_strings")
        .select("namespace,key,value,locale")
        .in("locale", codes);
      if (error) throw error;
      // Build map; prefer the requested locale, fall back to English.
      const map = new Map<string, string>();
      const fallback = new Map<string, string>();
      for (const r of (data ?? []) as (Row & { locale: string })[]) {
        const id = `${r.namespace}:${r.key}`;
        if (r.locale === locale) map.set(id, r.value);
        else if (r.locale === "en") fallback.set(id, r.value);
      }
      for (const [k, v] of fallback) if (!map.has(k)) map.set(k, v);
      return map;
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Translation hook. Usage:
 *   const { t } = useTranslations();
 *   t("common.save")                       // looks in `ui` namespace
 *   t("checkout.cta", "Buy now")           // with English fallback string
 *   t("nav.home", undefined, "site")       // explicit namespace
 */
export function useTranslations(defaultNamespace = "ui") {
  const { code } = useLocale();
  const { data: map } = useStringsFor(code);

  const t = useCallback(
    (key: string, fallback?: string, namespace: string = defaultNamespace) => {
      const id = `${namespace}:${key}`;
      return map?.get(id) ?? fallback ?? key;
    },
    [map, defaultNamespace],
  );

  return useMemo(() => ({ t, locale: code, ready: !!map }), [t, code, map]);
}
